import { Autocomplete } from "@mui/material";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSharedFields, updateSharedFields } from "store/companyGroups/company-groups-use-cases";
import { useAppDispatch, useAppSelector } from "store/store";

// Per-group selection of beneficiary fields shared across the group's companies.
const SharedFieldsEditor = ({ groupId }: { groupId: number }) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { sharedFields, sharedFieldsCatalog, sharedFieldsStatus } = useAppSelector((state) => state.companyGroups);

    const [draft, setDraft] = useState<string[]>([]);

    useEffect(() => {
        dispatch(fetchSharedFields(groupId));
    }, [dispatch, groupId]);

    // Reset the draft whenever the persisted selection changes (fetch / save).
    useEffect(() => {
        setDraft(sharedFields);
    }, [sharedFields]);

    // Beneficiary field labels are translated; fall back to the raw key.
    const label = (field: string) => t(`Beneficiary.${field}`, field);

    const isLoading = sharedFieldsStatus === "pending";

    if (isLoading && !sharedFieldsCatalog.length) {
        return <p className="text-gray-500">{t("CompanyGroups.Loading")}</p>;
    }

    return (
        <div className="flex flex-col gap-2">
            <h3 className="font-semibold">{t("CompanyGroups.SharedFields")}</h3>
            <p className="text-sm text-gray-500">{t("CompanyGroups.SharedFieldsHint")}</p>
            <Autocomplete
                multiple
                size="small"
                options={sharedFieldsCatalog}
                value={draft}
                onChange={(_e, value) => setDraft(value)}
                getOptionLabel={label}
                isOptionEqualToValue={(a, b) => a === b}
                renderInput={(params) => <StyledTextField {...params} placeholder={t("CompanyGroups.SharedFields")} />}
            />
            <StyledButton
                buttonType="primary"
                loading={isLoading}
                onClick={() => dispatch(updateSharedFields({ groupId, fields: draft }))}
            >
                {t("CompanyGroups.SaveSharedFields")}
            </StyledButton>
        </div>
    );
};

export default SharedFieldsEditor;
